import { FC, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Card, Title } from "shared/components";
import { GuestForm } from "Bands/components";
import { IAPIState, ICardTheme, IFormButtons, IStatus } from "shared/types";
import { copy } from "shared/assets";
import { Spacer } from "shared/layout";
import {
  BAND_ACTIONS,
  IEventGuestParams,
  IEventGuestStatus,
  IGuestlistBlock,
  IGuestlistBlockType,
} from "Bands/types";
import {
  clearActionMeta,
  createEventGuest,
  dataMetaSelector,
  dispatchGetEventGuests,
} from "Bands/state";
import { API_ENDPOINTS } from "shared/constants";
import {
  useClearActionMetaOnUnMount,
  useGuestlistNumberError,
} from "Bands/utils";
import { addTemporaryToast } from "shared/components/Toast";
import { getIdFromPath, mapUserPermissions } from "shared/utils";

const createGuestCopy = copy.guestlistBlock.guests.create;

interface CreateGuestProps {
  block: IGuestlistBlock;
}

export const CreateGuest: FC<CreateGuestProps> = ({
  block: {
    id: blockId,
    ticketAndPassTypes: ticketAndPasses,
    maxTicketsPerEntry,
    guestBlockType,
    permissions,
  },
}) => {
  const dispatch = useDispatch();
  const { api: apiMeta, data } = useSelector(
    dataMetaSelector(BAND_ACTIONS.createEventGuest)
  );

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [resetForm, setResetForm] = useState(false);

  // what guestlist block initiated the api call
  const apiBlockId = useMemo(
    () =>
      data?.guestListBlock
        ? getIdFromPath(
            data?.guestListBlock,
            API_ENDPOINTS.EVENT_GUESTLIST_BLOCKS
          )
        : "",
    [data]
  );

  useClearActionMetaOnUnMount(BAND_ACTIONS.createEventGuest);

  useEffect(() => {
    // ignore calls from other blocks
    if (!apiBlockId.includes(blockId)) return;

    if (apiMeta?.status === IAPIState.succeeded) {
      setIsSubmitted(false);
      setResetForm(true);

      dispatch(clearActionMeta(BAND_ACTIONS.createEventGuest));

      dispatchGetEventGuests({ dispatch, blockId });

      dispatch(
        addTemporaryToast({
          status: IStatus.success,
          message: createGuestCopy.messages.success,
        })
      );
    }
  }, [apiMeta?.status, blockId, data, apiBlockId, dispatch]);

  useEffect(() => {
    if (resetForm) {
      setResetForm(false);
    }
  }, [resetForm]);

  const defaultValues: IEventGuestParams = useMemo(
    () => ({
      nbGuests: 1,
      status:
        guestBlockType === IGuestlistBlockType.public
          ? IEventGuestStatus.approved
          : IEventGuestStatus.pending,
    }),
    [guestBlockType]
  );

  const buttons: IFormButtons = useMemo(
    () => ({
      primary: {
        size: "m",
        children: createGuestCopy.buttons.create,
        "data-testid": "submit_create_guest",
      },
    }),
    []
  );

  const { error } = useGuestlistNumberError(apiMeta?.error);

  const handleSubmit = ({ ticketAndPassTypes, ...rest }: IEventGuestParams) => {
    if (!ticketAndPassTypes) return;

    dispatch(
      createEventGuest({
        ...rest,
        ticketAndPassTypes: ticketAndPassTypes.map((id) =>
          API_ENDPOINTS.EVENT_TICKET_PASS(id)
        ),
        guestListBlock: API_ENDPOINTS.EVENT_GUESTLIST_BLOCK(blockId),
      })
    );

    setIsSubmitted(true);
  };

  return (
    <Card theme={ICardTheme.secondary}>
      <Spacer size="m">
        <Title asComponent="h3" size="m">
          {createGuestCopy.title}
        </Title>
        <GuestForm
          buttons={buttons}
          defaultValues={defaultValues}
          handleSubmit={handleSubmit}
          ticketAndPasses={ticketAndPasses}
          maxTicketsPerEntry={maxTicketsPerEntry}
          apiStatus={isSubmitted ? apiMeta?.status : undefined}
          apiError={isSubmitted ? apiMeta?.error : undefined}
          error={isSubmitted ? error : undefined}
          hideStatus={
            !mapUserPermissions(permissions || []).CHANGE_ENTRY_STATUS
          }
          resetForm={resetForm}
        />
      </Spacer>
    </Card>
  );
};
